import React from 'react';
import SPAButton from '../../shared/SPAButton';
import SPADialog from '../../shared/SPADialog';
import { ProgramUnit } from './types';
import { useRemoveUnit } from './useProgramLessons';

interface RemoveUnitDialogProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
  programId: number;
  unit: ProgramUnit;
}

const factClass = 'tw-m-0 tw-text-spa-tertiary';
const valueClass = 'tw-m-0 tw-font-bold tw-text-spa-heading';

// Says what the legacy "Are you sure?" left unsaid: the lessons stay (unitless), and the unit
// record is deleted only when this was its last program.
const RemoveUnitDialog = ({ onOpenChange, open, programId, unit }: RemoveUnitDialogProps) => {
  const remove = useRemoveUnit(programId);
  const lessonCount = unit.lessons.length;
  const otherPrograms = Math.max(unit.programs_count - 1, 0);

  const handleOpenChange = (next: boolean) => {
    if (!next) remove.reset();
    onOpenChange(next);
  };

  // The card unmounts with the cache patch, taking the dialog with it.
  const confirm = () => remove.mutate(unit);

  return (
    <SPADialog open={open} onOpenChange={handleOpenChange}>
      <SPADialog.Content size="sm">
        <SPADialog.Header>
          <SPADialog.Title>Remove unit from this program?</SPADialog.Title>
          <SPADialog.CloseButton />
        </SPADialog.Header>
        <SPADialog.Body className="tw-flex tw-flex-col tw-gap-3 tw-text-sm">
          <SPADialog.Description>
            The unit’s lessons stay in the program as unitless lessons. The unit itself is only deleted if
            no other program uses it.
          </SPADialog.Description>
          <dl
            className={[
              'tw-m-0 tw-grid tw-grid-cols-[auto_1fr] tw-gap-x-3 tw-gap-y-1 tw-rounded-md tw-p-3',
              'tw-border tw-border-solid tw-border-spa-border-secondary tw-bg-spa-bg',
            ].join(' ')}
          >
            <dt className={factClass}>Unit</dt>
            <dd className={valueClass}>{unit.title_with_position}</dd>
            <dt className={factClass}>Lessons kept</dt>
            <dd className={valueClass}>
              {lessonCount === 0 ? 'none' : `${lessonCount}, moved to unitless`}
            </dd>
            <dt className={factClass}>Other programs</dt>
            <dd className={valueClass}>
              {otherPrograms === 0 ? 'none — the unit will be deleted' : `${otherPrograms}, the unit stays there`}
            </dd>
          </dl>
          {remove.isError && (
            <p className="tw-m-0 tw-text-xs tw-leading-4 tw-text-spa-error" role="alert">
              The unit could not be removed. Try again.
            </p>
          )}
        </SPADialog.Body>
        <SPADialog.Footer>
          <SPADialog.Close asChild>
            <SPAButton>Cancel</SPAButton>
          </SPADialog.Close>
          <SPAButton disabled={remove.isLoading} variant="danger" onClick={confirm}>
            Remove unit
          </SPAButton>
        </SPADialog.Footer>
      </SPADialog.Content>
    </SPADialog>
  );
};

export default RemoveUnitDialog;
